<script setup lang="ts">
  import {
    computed,
    ref,
    nextTick,
    watch,
    onMounted,
    onUnmounted,
    inject,
    type ComputedRef,
  } from 'vue'
  import { onClickOutside } from '@vueuse/core'
  import { BaseButton } from '@qic/ui-components'
  import CrossIcon from '@/shared/components/icons/CrossIcon.vue'
  import BaseCheckbox from '@/shared/components/BaseCheckbox/BaseCheckbox.vue'
  import BaseChip from '@/shared/components/BaseChip/BaseChip.vue'
  import ArrowIcon from '@/shared/components/icons/ArrowIcon.vue'
  import {
    DEFAULT_OPTIONS_COUNT,
    DEFAULT_VALUE_KEY,
    DEFAULT_TITLE_KEY,
    MOBILE_WIDTH,
  } from './BaseMultiSelect.constants'
  import { ErrorsList } from '@/shared/components/ErrorsList'
  import type { Nullable } from '@/shared/types/common.types'
  import { useBodyScroll } from '@/shared/composables'

  type SelectOption = { [key: string]: any }

  interface Props {
    modelValue: string[]
    options: SelectOption[]
    visibleOptionsCount?: number
    label: string
    disabled?: boolean
    filterable?: boolean
    filterFunction?: Nullable<(option: SelectOption, search: string) => boolean>
    titleKey?: string
    valueKey?: string
    errors?: string[]
    inModal?: boolean
    mobileTitle?: string
  }

  interface Emits {
    (e: 'update:modelValue', value: any[]): void
    (e: 'change'): void
    (e: 'input', value: string): void
    (e: 'closeModal'): void
    (e: 'close'): void
    (e: 'open'): void
    (e: 'clear'): void
    (e: 'saveByModal'): void
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: () => [],
    options: () => [],
    visibleOptionsCount: DEFAULT_OPTIONS_COUNT,
    disabled: false,
    filterable: false,
    titleKey: DEFAULT_TITLE_KEY,
    valueKey: DEFAULT_VALUE_KEY,
    errors: () => [],
    filterFunction: null,
    inModal: false,
  })
  const emit = defineEmits<Emits>()

  const { allowBodyScroll, disableBodyScroll } = useBodyScroll()
  const selectListElement = ref<HTMLUListElement | null>(null)
  const selectElement = ref<HTMLDivElement | null>(null)
  const filterInputElement = ref<HTMLInputElement | null>(null)

  const isFilterDisabled = ref(false)
  const isRtl = inject('isRTL') as ComputedRef<boolean>

  const value = computed<string[]>({
    get() {
      return props.options.length ? props.modelValue : []
    },
    set(newValue: string[]) {
      emit('update:modelValue', newValue)
      emit('change')
    },
  })

  const hasValue = computed(() => !!value.value.length)
  const isSelectSearchable = computed(() => props.filterable)

  const optionsMap = computed(
    () => new Map(props.options.map((option) => [String(option[props.valueKey]), option]))
  )
  const selectedOptions = computed<SelectOption[]>(() => {
    const res: SelectOption[] = []
    value.value.forEach((optionValue: string) => {
      const option = optionsMap.value.get(optionValue)
      if (option) res.push(option)
    })
    return res
  })

  const inputFilter = ref('')
  const filteredOptions = computed(() => {
    if (props.filterFunction) {
      return props.options.filter((option: SelectOption) =>
        props.filterFunction?.(option, String(inputFilter.value).toLowerCase())
      )
    }

    if (props.filterable && !!inputFilter.value && !isFilterDisabled.value) {
      const query = String(inputFilter.value).toLowerCase()
      return props.options.filter((option: SelectOption) => {
        const comparableOption = option[props.titleKey].toLowerCase()
        return comparableOption.includes(query)
      })
    } else {
      return props.options
    }
  })

  const isInputInvalid = computed(() => props.errors.length > 0)

  const onInput = (event: Event) => {
    const target = event.target
    if (!(target instanceof HTMLInputElement)) return

    const validRegex = /^[a-zA-Z\u0600-\u06FF\s]*$/
    if (!validRegex.test(target.value)) {
      target.value = target.value.replace(/[^a-zA-Z\u0600-\u06FF\s]/g, '')
    }

    if (props.filterable) {
      isFilterDisabled.value = false
      selectListElement.value?.scroll(0, 0)
    }
    inputFilter.value = target.value
    emit('input', target.value)
  }

  const focusInput = () => {
    if (props.filterable && optionsIsActive.value) nextTick(() => filterInputElement.value?.focus())
  }

  const optionsIsActive = ref(false)
  onClickOutside(selectElement, () => {
    optionsIsActive.value = false
  })
  const toggleActive = (force: Event | boolean = false) => {
    const forceToggle = force instanceof Event ? false : force

    if (!(isSelectSearchable.value && optionsIsActive.value) || forceToggle) {
      optionsIsActive.value = !optionsIsActive.value

      if (optionsIsActive.value) {
        if (isSelectSearchable.value) focusInput()
        if (props.filterable) isFilterDisabled.value = true
        const selectedIndex = selectedOptions.value.length
          ? filteredOptions.value.indexOf(selectedOptions.value[selectedOptions.value.length - 1])
          : 0

        if (isMobile.value) {
          disableBodyScroll()
        }

        nextTick(() => scrollToSelected(selectedIndex))
      } else {
        if (isMobile.value) {
          allowBodyScroll()
        }
      }
    }
    focusInput()
  }

  const selectOption = (option: SelectOption) => {
    const optionValue = String(option[props.valueKey])
    const index = value.value.indexOf(optionValue)
    const res = [...value.value]
    if (index === -1) {
      res.push(optionValue)
    } else {
      res.splice(index, 1)
    }
    value.value = res
  }

  const onDeleteOption = (optionValue: string) => {
    const index = value.value.indexOf(optionValue)
    const res = [...value.value]
    res.splice(index, 1)
    value.value = res
  }

  const onClear = () => {
    value.value = []
    if (props.filterable) {
      inputFilter.value = ''
      focusInput()
    }

    emit('clear')
  }

  const scrollToSelected = (index: number) => {
    const firstElement = selectListElement.value?.children[0]
    if (!firstElement) return
    const optionHeight = firstElement.getBoundingClientRect().height
    nextTick(() => {
      selectListElement.value?.scroll(0, optionHeight * index)
    })
  }

  const heightOptionWindowStyle = computed(() => {
    if (optionsIsActive.value) {
      const firstElement = selectListElement.value?.children[0]?.getBoundingClientRect().height
      if (!firstElement) return ''
      return `max-height: ${firstElement * props.visibleOptionsCount}px `
    }
    return ''
  })

  const nullValueIfNoOption = () => {
    if (value.value.length && !props.options.length) value.value = []
  }

  const onClose = () => {
    optionsIsActive.value = false

    if (isMobile.value) {
      allowBodyScroll()
    }

    emit('closeModal')
  }

  const onSave = () => {
    onClose()
    emit('saveByModal')
  }

  const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_WIDTH})`)
  const isMobile = ref(mediaQuery.matches)
  const handleResize = (event: MediaQueryListEvent) => {
    isMobile.value = event.matches
  }
  const isMobileSelectShowed = computed(() => isMobile.value && optionsIsActive.value)

  onMounted(() => {
    mediaQuery.addEventListener('change', handleResize)
    if (props.inModal) toggleActive()
  })

  onUnmounted(() => {
    mediaQuery.removeEventListener('change', handleResize)
  })

  watch(
    () => value.value,
    () => {
      nullValueIfNoOption()
      focusInput()
      inputFilter.value = ''
    }
  )

  watch(
    () => props.options,
    () => {
      nullValueIfNoOption()
    }
  )

  watch(
    () => optionsIsActive.value,
    (value: boolean) => {
      if (value) {
        emit('open')
      } else {
        emit('close')
      }
    }
  )
</script>

<template>
  <Teleport to="body" :disabled="!(isMobileSelectShowed || inModal)">
    <div
      v-if="!isMobileSelectShowed && inModal"
      class="multiselect__modal-bg"
      @click="onClose"
    ></div>
    <div
      ref="selectElement"
      data-testid="BaseMultiSelect"
      tabindex="0"
      class="multiselect-wrapper"
      :class="{
        mobile: isMobileSelectShowed,
        modal: !isMobileSelectShowed && inModal,
        rtl: isRtl,
      }"
    >
      <div v-show="isMobileSelectShowed || inModal" class="multiselect__mobile-header">
        <div class="multiselect__mobile-header-title">{{ mobileTitle }}</div>
        <CrossIcon class="multiselect__mobile-header-close" @click="onClose" />
      </div>
      <div class="multiselect-control-wrapper">
        <div
          class="multiselect-control"
          :class="{
            'multiselect-control_active': optionsIsActive && !value.length,
            'multiselect-control_has-value': hasValue,
            'multiselect-control_disabled': disabled,
            'multiselect-control_invalid': isInputInvalid,
          }"
          @click="toggleActive"
        >
          <span v-if="label" class="multiselect-control__label">
            {{ label }}
          </span>
          <div class="multiselect-control__tags">
            <BaseChip
              v-for="option of selectedOptions"
              :key="option[props.valueKey]"
              :text="option[props.titleKey]"
              is-deleteable
              @click.stop
              @delete="onDeleteOption(option[props.valueKey])"
            />
            <div class="multiselect-control__input-container">
              <input
                v-show="filterable && optionsIsActive"
                ref="filterInputElement"
                type="text"
                :disabled="!(filterable && optionsIsActive)"
                class="multiselect-control__input"
                :value="inputFilter"
                @input="onInput"
                @mousedown.prevent
              />
            </div>
          </div>
          <div class="multiselect-control__buttons">
            <!-- TODO: Should be a component. It is used in BaseChip too -->
            <button
              v-if="hasValue || (optionsIsActive && (hasValue || filterable))"
              class="multiselect-control__cross"
              type="button"
              @mousedown="onClear"
            />
            <ArrowIcon
              class="multiselect-control__icon"
              :class="{ 'multiselect-control__icon_active': optionsIsActive }"
              @click.stop="toggleActive(true)"
            />
            <slot name="buttons"></slot>
          </div>
        </div>
      </div>

      <Transition name="fade">
        <ul
          v-if="optionsIsActive"
          ref="selectListElement"
          class="multiselect-options"
          :style="heightOptionWindowStyle"
        >
          <li
            v-for="option of filteredOptions"
            :key="option[props.valueKey]"
            class="multiselect__option"
            :class="{ multiselect__option_selected: selectedOptions.includes(option) }"
            @click="selectOption(option)"
          >
            <slot name="option" v-bind="option">
              <span class="multiselect__option-title">
                {{ option[props.titleKey] }}
              </span>
            </slot>
            <BaseCheckbox
              :id="String(option[props.valueKey])"
              v-model="value"
              :name="option[props.titleKey]"
              @click.stop
            />
          </li>
        </ul>
      </Transition>
      <ErrorsList v-if="isInputInvalid" :errors="errors" />
      <div v-if="isMobileSelectShowed || inModal" class="multiselect__mobile-button">
        <BaseButton :medium="isMobileSelectShowed || !inModal" icon="none" @click="onSave">
          {{ $t('qic_dropdown_save_button') }}
        </BaseButton>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss">
  @import './BaseMultiSelect';
</style>
